import { useEffect, useState, Dispatch, SetStateAction } from 'react';

export const useSyncedLocalStorage = <T>(
	key: string,
	initialValue: T
): [T, Dispatch<SetStateAction<T>>] => {
	const readValue = (): T => {
		if (typeof window === 'undefined') return initialValue;

		try {
			const item = localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			console.warn(`Error reading localStorage key “${key}”:`, error);
			return initialValue;
		}
	};

	const [storedValue, setStoredValue] = useState<T>(readValue);

	const setValue: Dispatch<SetStateAction<T>> = (value) => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value;

			setStoredValue(valueToStore);
			localStorage.setItem(key, JSON.stringify(valueToStore));

			window.dispatchEvent(
				new CustomEvent('local-storage', {
					detail: {
						key,
						newValue: JSON.stringify(valueToStore),
					},
				})
			);
		} catch (error) {
			console.warn(`Error setting localStorage key “${key}”:`, error);
		}
	};

	useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key === key && event.newValue) {
				setStoredValue(JSON.parse(event.newValue));
			}
		};

		const handleCustomEvent = (event: Event) => {
			const customEvent = event as CustomEvent;
			if (customEvent.detail?.key === key) {
				setStoredValue(JSON.parse(customEvent.detail.newValue));
			}
		};

		window.addEventListener('storage', handleStorageChange);
		window.addEventListener('local-storage', handleCustomEvent);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('local-storage', handleCustomEvent);
		};
	}, [key]);

	return [storedValue, setValue];
};
